import { calcularDataLimite, formatDataBr } from "@/modules/recursos";
import { useMemo } from "react";
import { periodoDaObraNoWizard, readEquipe } from "../../../hooks/nova-obra.equipe";
import { useAlocacoesPeriodo } from "../../../hooks/use-alocacoes-periodo";
import { useNovaObra } from "../../../hooks/nova-obra.context";
import { useEquipeObraOpcoes } from "@/modules/recursos";
import { todayIso } from "@/modules/carga-trabalho";
import { conflitosPorProfissional, novasDaEquipe } from "../../../hooks/equipe-verificacoes";
import { REVISAO } from "../../../nova-obra.constants";
import type { SecaoEquipeProps } from "./types-secao-equipe";
import { detectarConflitos } from "@/modules/recursos";

export function useSecaoEquipe({ onEditar }: SecaoEquipeProps) {
  const { state } = useNovaObra();
  const periodo = useMemo(
    () => periodoDaObraNoWizard(state.etapas.duracao),
    [state.etapas.duracao],
  );
  const today = useMemo(() => todayIso(), []);
  const opcoes = useEquipeObraOpcoes(periodo?.inicio ?? today, periodo?.fim ?? today);
  const linhas = useMemo(
    () =>
      readEquipe(state.etapas.equipe).linhas.filter((linha) => linha.usuarioId && !linha.editing),
    [state.etapas.equipe],
  ); // Conflito com outras obras (RN15): mesma consulta e regra da Etapa 4, também quando a Revisão
  // abre pelo stepper sem passar por ela. Falha na consulta só omite o aviso: não bloqueia.
  const carga = useAlocacoesPeriodo({
    periodo: { inicio: periodo?.inicio ?? today, fim: periodo?.fim ?? today },
    profissionalIds: periodo ? linhas.flatMap((linha) => linha.usuarioId ?? []) : [],
  });
  const conflitos = useMemo(() => {
    if (!periodo || carga.status !== "success") return [];
    const papeisById = new Map(opcoes.papeis.map((item) => [item.id, item]));
    return conflitosPorProfissional(
      detectarConflitos(
        novasDaEquipe(linhas, papeisById),
        { id: "", codigo: "", nome: "" },
        carga.profissionais,
      ),
    );
  }, [periodo, carga.status, carga.profissionais, linhas, opcoes.papeis]);
  const papel = (id: string) => opcoes.papeis.find((item) => item.id === id);
  const pendentes = opcoes.papeis.filter(
    (item) => item.obrigatorio && !linhas.some((linha) => linha.papelObraId === item.id),
  );

  return {
    linhas: linhas.map((linha) => ({
      id: linha.id,
      profissionalNome: opcoes.profissionais.find((item) => item.id === linha.usuarioId)?.nome,
      papelNome: papel(linha.papelObraId)?.nome,
      inicioFormatado: formatDataBr(linha.inicio),
      fimFormatado: formatDataBr(linha.fim),
    })),
    conflitos: conflitos.map((item) => ({
      usuarioId: item.usuarioId,
      testId: `revisao-conflito-${item.usuarioId}`,
      texto: REVISAO.equipe.conflito(
        item.profissional,
        item.periodos.map((intervalo) => ({
          inicio: formatDataBr(intervalo.inicio),
          fim: formatDataBr(intervalo.fim),
        })),
      ),
    })),
    pendentes: pendentes.map((item) => {
      const limite = periodo ? calcularDataLimite(item.janela, periodo.inicio, periodo.fim) : null;
      return {
        id: item.id,
        testId: `revisao-papel-pendente-${item.id}`,
        texto: limite
          ? REVISAO.equipe.papelPendente(item.nome, formatDataBr(limite.data), limite.rotulo)
          : REVISAO.equipe.papelPendenteSemPrazo(item.nome),
      };
    }),
    onEditar,
  };
}
